// Sites product analytics: the event names, the properties each one may carry,
// and one `trackSites()` that sends them through the shared `track()` facade.
//
// Names are snake_case with a `sites_` prefix (unlike the "Object Action" names
// in events.js) so every Sites event filters with one `sites_*` match in PostHog.
// Fire only from the success path, after the API call resolved.
//
// Properties are an allowlist per event: anything a caller passes that is not
// listed is dropped, so viewer data, emails, titles or full hostnames cannot
// leak in by accident. Custom domains travel as their registrable domain only.
// Every event also gets `site_id` and `template`; the plan tier (`plan`,
// `plan_category`, `is_trial`) already rides along as a super property set by
// identifyUser().
import store from '@/store'
import { siteTemplate } from '@/components/Sites/siteTemplates'
import { registrableDomain } from '@/Utils/siteDomains'
import { track } from './posthog'

export const SITES_EVENTS = Object.freeze({
	SITE_CREATED: 'sites_site_created',
	SITE_PUBLISHED: 'sites_site_published',
	SITE_DELETED: 'sites_site_deleted',
	TEMPLATE_SWITCHED: 'sites_template_switched',
	CONTENT_ADDED: 'sites_content_added',
	CONTENT_ACCESS_CHANGED: 'sites_content_access_changed',
	PLAN_CREATED: 'sites_plan_created',
	PROMO_CREATED: 'sites_promo_created',
	CUSTOM_DOMAIN_ADDED: 'sites_custom_domain_added',
	CUSTOM_DOMAIN_VERIFIED: 'sites_custom_domain_verified',
	CUSTOM_DOMAIN_SET_PRIMARY: 'sites_custom_domain_set_primary',
	CUSTOM_DOMAIN_REMOVED: 'sites_custom_domain_removed',
	GIVING_ENABLED: 'sites_giving_enabled',
	THEME_PRESET_APPLIED: 'sites_theme_preset_applied',
	AUDIENCE_PRESET_SET: 'sites_audience_preset_set',
	REGISTRATION_FIELDS_UPDATED: 'sites_registration_fields_updated',
	PREVIEW_OPENED: 'sites_preview_opened',
	UPGRADE_PROMPT_SHOWN: 'sites_upgrade_prompt_shown',
})

export const SITES_UPGRADE_REASONS = Object.freeze({
	SITES_NOT_IN_PLAN: 'sites_not_in_plan',
	SITES_LIMIT_REACHED: 'sites_limit_reached',
	CUSTOM_DOMAINS_NOT_IN_PLAN: 'custom_domains_not_in_plan',
	CUSTOM_DOMAINS_LIMIT_REACHED: 'custom_domains_limit_reached',
})

const E = SITES_EVENTS
const EVENT_PROPS = Object.freeze({
	[E.SITE_CREATED]: ['from', 'slug', 'sites_used', 'max_sites'],
	[E.SITE_PUBLISHED]: ['published_changes'],
	[E.SITE_DELETED]: [],
	[E.TEMPLATE_SWITCHED]: ['from', 'to'],
	[E.CONTENT_ADDED]: ['type', 'access'],
	[E.CONTENT_ACCESS_CHANGED]: ['from', 'to', 'items'],
	[E.PLAN_CREATED]: ['scope', 'price', 'currency', 'recurring', 'period'],
	[E.PROMO_CREATED]: ['discount_type', 'all_plans'],
	[E.CUSTOM_DOMAIN_ADDED]: ['domain', 'apex'],
	[E.CUSTOM_DOMAIN_VERIFIED]: ['domain', 'method'],
	[E.CUSTOM_DOMAIN_SET_PRIMARY]: ['domain'],
	[E.CUSTOM_DOMAIN_REMOVED]: ['domain'],
	[E.GIVING_ENABLED]: ['mode'],
	[E.THEME_PRESET_APPLIED]: ['preset'],
	// preset: church | event | general; surface: create | settings
	[E.AUDIENCE_PRESET_SET]: ['preset', 'surface'],
	// count of owner fields only — never labels, options or viewer answers
	[E.REGISTRATION_FIELDS_UPDATED]: ['count', 'gate'],
	[E.PREVIEW_OPENED]: [],
	[E.UPGRADE_PROMPT_SHOWN]: ['reason', 'surface', 'sites_used', 'max_sites', 'custom_domains_used', 'max_custom_domains'],
})

const DEBUG = process.env.VUE_APP_API_ENV !== 'prod'

function resolveSite(site) {
	if (site && typeof site === 'object') return site
	if (!site) return null
	return store.getters['Sites/siteById']?.(site) || null
}

/**
 * @param {string} event one of SITES_EVENTS
 * @param {object|string|null} site the site (or its id) the event is about
 * @param {object} [props] event properties; only the ones listed for the event are sent
 */
export function trackSites(event, site, props = {}) {
	try {
		const allowed = EVENT_PROPS[event]
		if (!allowed) {
			if (DEBUG) console.warn('[analytics] unknown Sites event', event)
			return
		}
		const s = resolveSite(site)
		const out = {
			site_id: s?.id ?? (typeof site === 'string' ? site : null),
			template: props.template ?? (s ? siteTemplate(s) : null),
		}
		for (const key of allowed) {
			if (props[key] !== undefined) out[key] = props[key]
		}
		if (out.domain) out.domain = registrableDomain(out.domain)
		track(event, out)
	} catch (e) {
		if (DEBUG) console.warn('[analytics] Sites track failed', event, e)
	}
}
